import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApolloQueryResult } from 'apollo-client';
import { NGXLogger } from 'ngx-logger';
import { Observable } from 'rxjs';

import { ApproveStatusEnum } from '../enum/approve-status.enum';
import { TDStoreValidatorTypeEnum } from '../enum/merchant-validator-type.enum';
import { NewRequestStatusEnum } from '../enum/request-status.enum';
import { productClassesQuery } from '../gql/classes.gql';
import { productGradingQuery } from '../gql/product-grading.gql';
import { MetricAction, MetricActionType, MetricModule, MetricType, Metrics } from '../models/metrics';
import {
  ApproveShelfRequest,
  RequestId,
  ShelfFixAssetCreateRequest,
  ShelfFixAssetRequestListResponse,
  ShelfFixAssetRequestListSearchCriteria,
  ShelfFixAssetRequestViewResponse
} from '../models/shelf-request.model';
import { BaseService } from './base.service';
import { MasterService } from './master.service';

@Injectable()
export class ShelfFixAssetRequestService extends BaseService {
  public headers: HttpHeaders;

  constructor(
    private readonly http: HttpClient,
    private readonly masterService: MasterService,
    protected readonly logger: NGXLogger
  ) {
    super();
    this.envService = this.env.services.shelfFixAssetRequest;
    this.headers = new HttpHeaders(this.envService.headers);
  }

  public getShelfValidate(
    type: TDStoreValidatorTypeEnum,
    value: string,
    urlParams: { [key: string]: string } = null
  ): Observable<HttpResponse<any>> {
    const url = this.getFullUrl(this.envService.validate, { type, value });
    const params = urlParams ? this.getParams(urlParams) : null;

    return this.http.get<any>(url, {
      headers: this.headers,
      observe: 'response',
      params
    });
  }

  public searchByCriteriaFixAsset(
    criteria: ShelfFixAssetRequestListSearchCriteria
  ): Observable<ShelfFixAssetRequestListResponse> {
    const metricsReq: Metrics = {
      metrics: [
        {
          metricType: MetricType.RESPONSE_TIME,
          module: MetricModule.SHELF_FIX_ASSET,
          route: this.envService.url,
          action: MetricActionType.SEARCH
        }
      ]
    };
    const params = this.getParams(criteria);

    return this.http.get<ShelfFixAssetRequestListResponse>(this.getUrl(), {
      headers: this.loaderHeaders(null, metricsReq),
      observe: 'body',
      params
    });
  }

  public saveRequestFixAsset(shelfRequest: ShelfFixAssetCreateRequest): Observable<ShelfFixAssetRequestViewResponse> {
    const url = this.getFullUrl(this.envService.save);

    return this.http.put<ShelfFixAssetRequestViewResponse>(url, shelfRequest, { headers: this.loaderHeaders(2000) });
  }

  public submitRequestFixAsset(
    shelfRequest: ShelfFixAssetCreateRequest
  ): Observable<ShelfFixAssetRequestViewResponse> {
    const url = this.getFullUrl(this.envService.submit);

    let metricsReq: Metrics;
    if (shelfRequest.status === NewRequestStatusEnum.DRAFT) {
      metricsReq = {
        metrics: [
          {
            metricType: MetricType.COUNTER,
            module: MetricModule.SHELF_FIX_ASSET,
            path: [this.envService.url, this.envService.submit].join('')
          }
        ]
      };
    }

    return this.http.post<ShelfFixAssetRequestViewResponse>(url, shelfRequest, {
      headers: this.loaderHeaders(2000, metricsReq)
    });
  }

  public getShelfRequestById(requestId: RequestId): Observable<ShelfFixAssetRequestViewResponse> {
    const url = this.getFullUrl(this.envService.view, {
      requestId: requestId.requestId
    });

    return this.http.get<any>(url, {
      headers: this.headers,
      observe: 'body'
    });
  }

  public deleteByRequestId(requestId: RequestId): Observable<HttpResponse<any>> {
    const url = this.getFullUrl(this.envService.delete, {
      requestId: requestId.requestId
    });

    const metricsReq: Metrics = {
      metrics: [
        {
          metricType: MetricType.COUNTER,
          module: MetricModule.SHELF_FIX_ASSET,
          path: [this.envService.url, 'delete'].join('')
        },
        {
          metricType: MetricType.HISTOGRAM,
          module: MetricModule.SHELF_FIX_ASSET,
          action: MetricAction.SHELF_FIX_ASSET_DELETE,
          type: 'shelf_fix_asset',
          items: 1
        }
      ]
    };

    return this.http.delete<any>(url, { headers: this.loaderHeaders(2000, metricsReq), observe: 'body' });
  }

  public approveRequest(request: ApproveShelfRequest): Observable<ShelfFixAssetRequestViewResponse> {
    const url = this.getFullUrl(this.envService.approve);

    const metricsReq: Metrics = {
      metrics: [
        {
          metricType: MetricType.COUNTER,
          module: MetricModule.SHELF_FIX_ASSET,
          path: [this.envService.url, this.envService.approve].join('')
        },
        {
          metricType: MetricType.HISTOGRAM,
          module: MetricModule.SHELF_FIX_ASSET,
          action: this.getActionStatus(request.status),
          type: 'shelf_fix_asset',
          items: 1
        }
      ]
    };

    return this.http.put<ShelfFixAssetRequestViewResponse>(url, request, {
      headers: this.loaderHeaders(2000, metricsReq),
      observe: 'body'
    });
  }

  public getHistoryLogs(requestId: RequestId): Observable<any> {
    const url = this.getFullUrl(this.envService.history, {
      requestId: requestId.requestId
    });
    return this.http.get<any>(url, { headers: this.headers, observe: 'body' });
  }

  public getProductGrading(): Observable<ApolloQueryResult<any>> {
    return this.masterService.watchQuery({ query: productGradingQuery });
  }

  public getClasses(): Observable<ApolloQueryResult<any>> {
    return this.masterService.watchQuery({ query: productClassesQuery });
  }

  public exportShelf(selected: Array<string>): Observable<any> {
    const url = this.getFullUrl(this.envService.export);

    const metricsReq: Metrics = {
      metrics: [
        {
          metricType: MetricType.COUNTER,
          module: MetricModule.SHELF_FIX_ASSET,
          path: `${this.envService.url}${this.envService.export}`,
          action: MetricActionType.EXPORT
        },
        {
          metricType: MetricType.RESPONSE_TIME,
          module: MetricModule.SHELF_FIX_ASSET,
          route: `${this.envService.url}${this.envService.export}`,
          action: MetricActionType.EXPORT
        }
      ]
    };
    return this.http.post<any>(
      url,
      { selected },
      {
        headers: this.loaderHeaders(0, metricsReq),
        observe: 'body',
        responseType: 'blob' as 'json'
      }
    );
  }

  public getActionStatus(status: string): MetricAction {
    let result: MetricAction;
    switch (status) {
      case ApproveStatusEnum.APPROVED:
        result = MetricAction.SHELF_FIX_ASSET_APPROVE;
        break;
      case ApproveStatusEnum.REJECTED:
        result = MetricAction.SHELF_FIX_ASSET_REJECT;
        break;
      case ApproveStatusEnum.CANCELLED:
        result = MetricAction.SHELF_FIX_ASSET_CANCEL_REQUEST;
        break;
      default:
        break;
    }
    return result;
  }
}
